import { useState, useEffect, useCallback, useMemo } from 'react'

import { useForm } from 'react-hook-form'

import { debounce } from 'throttle-debounce'

import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import I18n from 'Pods/Shared/I18n'

import showErrorNotification from 'Utils/showErrorNotification'
import getUtmCookies from 'Utils/utmCookies'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function isValidEmail(email) {
  return email && EMAIL_REGEX.test(email)
}

export function useRegister({
  captchaId,
  source,
  fromCheckout,
  locale,
  showEmailAvailability = false,
  onSuccess = null,
  defaultEmail = '',
}) {
  // Memorize translate function to prevent infinite loops in useMemo dependencies
  const translate = useCallback(
    (key) => I18n.t(key, { ns: 'rails', lng: locale }),
    [locale]
  )

  const { getData } = useApiGet()
  const { postData } = useApiPost()
  const [password, setPassword] = useState({ value: '', isValid: false })

  const [gdpr, setGdpr] = useState(true)
  const [registrationCaptchaEnabled, setRegistrationCaptchaEnabled] = useState(false)
  const [availabilityNotifications, setAvailabilityNotifications] = useState({
    email: { message: null, type: 'notification' },
  })
  const [passwordError, setPasswordError] = useState('')

  function onPasswordChange(state) {
    setPassword({ value: state.password, isValid: state.isValid })
    if (!state.isValid) {
      if (state.password.length <= 5) {
        setPasswordError(translate('register.validation.password_too_short'))
      } else {
        setPasswordError(translate('register.validation.password_too_weak'))
      }
    } else {
      setPasswordError('')
    }
  }

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, touchedFields },
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      email: defaultEmail,
      email_confirmation: '',
      password: '',
    },
  })

  const emailValue = watch('email')

  useEffect(() => {
    setRegistrationCaptchaEnabled(
      window.gon?.features?.rcaptcha || window.pageConfig?.features?.rcaptcha
    )
  }, [])

  const checkEmailAvailability = useMemo(
    () => debounce(200, (email) => {
      if (!isValidEmail(email)) return

      getData({
        path: '/api/internal/users/email/availability',
        params: { email },
        onSuccess: (response) => {
          if (!response?.data) return

          if (response.data.success) {
            setAvailabilityNotifications({
              email: { message: translate('register.email_available'), type: 'notification' },
            })
          } else {
            setAvailabilityNotifications({
              email: { message: translate('register.email_taken'), type: 'error' },
            })
          }
        },
      })
    }),
    [getData, translate]
  )

  useEffect(() => {
    if (isValidEmail(emailValue)) {
      checkEmailAvailability(emailValue)
    }
  }, [emailValue, checkEmailAvailability])

  function onSubmitSuccess(response) {
    if (typeof response.data === 'string') {
      document.write(response.data)
      return
    }

    if (onSuccess) {
      onSuccess(response)
      return
    }

    if (fromCheckout) {
      window.location = '/cg/checkout/payment'
    } else {
      window.location = response.data.redirectTo
    }
  }

  function onSubmitFailure(xhr) {
    if (registrationCaptchaEnabled) {
      window.mtcaptcha.resetUI(captchaId)
    }

    showErrorNotification(xhr.response?.data?.errors)
  }

  function onFormSubmit(data) {
    const hasAvailabilityErrors = availabilityNotifications.email.type === 'error'

    if (hasAvailabilityErrors || !password.isValid) {
      return null
    }

    const utmCookies = getUtmCookies()

    const form = new FormData()

    form.append('new_user[email]', data.email)
    if (data.email_confirmation) {
      form.append('new_user[email_confirmation]', data.email_confirmation)
    }
    form.append('new_user[password]', password.value)
    form.append('new_user[gdpr_consent]', gdpr ? 'opt_in' : 'opt_out')
    form.append('new_user[source]', source)
    form.append('from_checkout', fromCheckout)

    if (registrationCaptchaEnabled) {
      form.append('MTCaptchaToken', window.mtcaptcha.getVerifiedToken(captchaId))
    }

    form.append('utm_statistics[referrer]', utmCookies.referrer)
    form.append('utm_statistics[raw_utm_string]', utmCookies.raw_utm_string)

    postData({
      path: '/users',
      data: form,
      onSuccess: onSubmitSuccess,
      onError: onSubmitFailure,
    })
  }

  function onGdprToggle(event, newChecked) {
    setGdpr(newChecked)
  }

  function omniAuthParams() {
    const utmCookies = getUtmCookies()
    const gdprValue = gdpr ? 'opt_in' : 'opt_out'
    const rawUtmString = encodeURIComponent(utmCookies.raw_utm_string)

    return `gdpr_consent=${gdprValue}&source=${source}&referrer=${utmCookies.referrer}&raw_utm_string=${rawUtmString}`
  }

  function isEmailWarning() {
    return (errors.email && touchedFields.email)
      || availabilityNotifications.email.type === 'error'
  }

  function isEmailSuccess() {
    return availabilityNotifications.email.type === 'notification'
      && availabilityNotifications.email.message
  }

  function getEmailHelpText() {
    // Show validation errors first
    if (errors.email && touchedFields.email) {
      return errors.email.message
    }

    // Show availability error (email taken)
    if (availabilityNotifications.email.type === 'error' && availabilityNotifications.email.message) {
      return availabilityNotifications.email.message
    }

    // Show success message only if showEmailAvailability is enabled (checkout context)
    if (showEmailAvailability && isEmailSuccess()) {
      return availabilityNotifications.email.message
    }

    return ''
  }

  const token = document.querySelector('meta[name="csrf-token"]')?.content

  return {
    token,
    control,
    gdpr,
    registrationCaptchaEnabled,
    passwordError,
    handleSubmit,
    onFormSubmit,
    onPasswordChange,
    onGdprToggle,
    omniAuthParams,
    getEmailHelpText,
    isEmailWarning,
    isEmailSuccess,
  }
}
